import React, { useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { INK, numFormat, shortDate, axisProps, ChartTooltip, Section } from '../analyticsUi';
import { COLORS, eur, eurFormat } from './profitFormat';

const RANGES = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '6 months' },
  { days: 365, label: '1 year' },
  { days: 0, label: 'All' },
];

export default function SpendVsInstalls({ rangeDays, onRangeChange, rangeDaily, rangeMarkers, markers, today, onAddMarker, onRemoveMarker }) {
  const [newMarker, setNewMarker] = useState({ date: '', note: '' });

  async function submit(e) {
    e.preventDefault();
    if (!newMarker.date || !newMarker.note.trim()) return;
    await onAddMarker(newMarker.date, newMarker.note.trim());
    setNewMarker({ date: '', note: '' });
  }

  const markerLines = (showLabel) => rangeMarkers.map(m => (
    <ReferenceLine
      key={`${m.date}-${m.note}`}
      x={m.date}
      stroke={INK.secondary}
      strokeDasharray="3 3"
      label={showLabel ? { value: m.note, position: 'insideTopLeft', fill: INK.secondary, fontSize: 11 } : undefined}
    />
  ));

  return (
    <Section
      title="Spend vs installs"
      hint="Mark budget changes and see whether installs follow within a week or two."
      actions={
        <div className="pf-range">
          {RANGES.map(r => (
            <button key={r.days} className={`pf-range-btn ${rangeDays === r.days ? 'active' : ''}`} onClick={() => onRangeChange(r.days)}>
              {r.label}
            </button>
          ))}
        </div>
      }
    >
      <div className="pf-chart">
        <div className="pf-chart-title">Ad spend per day</div>
        <ResponsiveContainer width="100%" height={140}>
          <BarChart data={rangeDaily} syncId="daily" margin={{ top: 16, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={INK.grid} />
            <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={24} {...axisProps} />
            <YAxis tickFormatter={v => eurFormat.format(v)} width={64} {...axisProps} axisLine={false} />
            <Tooltip content={<ChartTooltip labelFormatter={shortDate} valueFormatter={v => eur(v, true)} />} cursor={{ fill: 'rgba(11,11,11,0.04)' }} />
            {markerLines(true)}
            <Bar isAnimationActive={false} dataKey="spend" name="Ad spend" fill={COLORS.spend} maxBarSize={24} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="pf-chart-title">Installs per day</div>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={rangeDaily} syncId="daily" margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={INK.grid} />
            <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={24} {...axisProps} />
            <YAxis allowDecimals={false} width={64} {...axisProps} axisLine={false} />
            <Tooltip content={<ChartTooltip labelFormatter={shortDate} valueFormatter={v => (v == null ? '–' : numFormat.format(Math.round(v * 10) / 10))} />} cursor={{ fill: 'rgba(11,11,11,0.04)' }} />
            <Legend itemSorter={null} iconType="circle" wrapperStyle={{ fontSize: 13, color: INK.secondary }} />
            {markerLines(false)}
            <Bar isAnimationActive={false} dataKey="installsOther" name="Other installs" stackId="i" fill={COLORS.installsOther} maxBarSize={24} stroke="#fcfcfb" strokeWidth={1} />
            <Bar isAnimationActive={false} dataKey="installsPaid" name="Credited to ads" stackId="i" fill={COLORS.installsPaid} maxBarSize={24} radius={[4, 4, 0, 0]} stroke="#fcfcfb" strokeWidth={1} />
            <Line isAnimationActive={false} dataKey="installsAvg" name="7-day average" stroke={COLORS.avg} strokeWidth={2} dot={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <form className="pf-marker-form" onSubmit={submit}>
        <input type="date" value={newMarker.date} max={today} onChange={e => setNewMarker(m => ({ ...m, date: e.target.value }))} />
        <input type="text" placeholder="What changed? e.g. paused ads, doubled budget" value={newMarker.note} onChange={e => setNewMarker(m => ({ ...m, note: e.target.value }))} />
        <button type="submit" className="btn-secondary" disabled={!newMarker.date || !newMarker.note.trim()}>Add marker</button>
      </form>
      {markers.length > 0 && (
        <ul className="pf-markers">
          {markers.map((m, i) => (
            <li key={`${m.date}-${m.note}`}>
              <span className="pf-marker-date">{shortDate(m.date)} {m.date.slice(0, 4)}</span>
              {m.note}
              <button className="pf-link-button" onClick={() => onRemoveMarker(i)} aria-label={`Remove marker ${m.note}`}>Remove</button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
